import type { FC } from "react";
import { useCallback } from "react";
import { useToken } from "../../server/auth/use-token";
import { ImagesService } from "../../server/cloudflare/images-service";
import type { UUID } from "../../utils/uuid";
import type { AssetResult } from "./hooks/use-upsert-asset";
import { useUpdateAssetCallback } from "./hooks/use-upsert-asset";
import type { Asset } from "./server/digital-asset-service";
import { UploadFileModal } from "./upload-file-modal";

export const ReplaceFileModal: FC<{
  open: boolean;
  assetToReplace: Asset;
  onOpen: () => void;
  onClose: () => void;
  onCancel: () => void;
  onChange: () => void;
  onViewFileClick: (asset: Asset) => void;
}> = ({ assetToReplace, open, ...props }) => {
  const updateAsset = useUpdateAssetCallback();
  const getToken = useToken();

  function getUuid(url: string): string {
    const withoutQuery = url.split("?")[0] ?? url;
    const withoutTrailingSlash = withoutQuery.endsWith("/")
      ? withoutQuery.slice(0, -1)
      : withoutQuery;

    const parts = withoutTrailingSlash.split("/");
    return parts[parts.length - 1] ?? "";
  }

  const handleSave = useCallback(
    async (asset: {
      title: string;
      thumbnailUrl?: string | null;
      contentType: string;
      projectId?: string | null;
      pageId?: string | null;
      mimeType?: string | null;
      sourceDigitalAssetId?: UUID | null;
      publicUrl?: string | null;
      externalStorageId?: string | null;
    }): Promise<AssetResult> => {
      if (!asset.publicUrl || !assetToReplace.public_url)
        throw new Error("Asset must have a public url");

      let url = asset.publicUrl;

      if (!asset.mimeType?.startsWith("video/")) {
        const id = getUuid(assetToReplace.public_url);
        if (!id) throw new Error("Asset must have a well-formed public url");
        const blob = await (await fetch(asset.publicUrl)).blob();
        const mimeType = asset.mimeType || blob.type;
        const file = new File([blob], id, mimeType ? { type: mimeType } : undefined);
        const { success, url: imageServiceUrl } = await new ImagesService().upload(
          file,
          id,
          await getToken(),
          mimeType,
          true,
        );
        if (!success) throw new Error("Failed to upload image");

        url = `${imageServiceUrl}?v=${Date.now()}`;
      }

      return await updateAsset({
        asset: assetToReplace,
        title: asset.title,
        ...(asset.thumbnailUrl ? { thumbnailUrl: asset.thumbnailUrl } : {}),
        publicUrl: url,
        externalStorageId: asset.externalStorageId,
      });
    },
    [assetToReplace, getToken, updateAsset],
  );

  const allowedFileTypes = (asset: Asset): ("image" | "video" | "pdf")[] => {
    if (!asset) return [];
    if (asset.mime_type?.startsWith("image/")) return ["image"];
    if (asset.mime_type?.startsWith("video/")) return ["video"];
    return ["pdf"];
  };

  return (
    open && (
      <UploadFileModal
        open
        heading={`Replace ${assetToReplace.title}`}
        allowedFileTypes={allowedFileTypes(assetToReplace)}
        onSave={handleSave}
        assetToReplaceId={assetToReplace.id}
        {...props}
      />
    )
  );
};
